import type { BackendError, Estimate } from '@/features/consignment-form/contract'
import { formatBackendError, itemIssueLine } from '@/features/consignment-form/labels'
import type { Item } from '@/features/consignment-form/schema'
import { t } from '@/lang'

export type EstimateState =
  | { status: 'loading' }
  | { status: 'ready'; value: Estimate }
  | {
      status: 'unavailable'
      reason: 'temporary' | 'rate_limited' | 'access' | 'invalid_item'
      retryAt?: number
      backend?: BackendError
    }

/** What the total line can say about the saved rows as a whole. */
export type EstimateStatus = 'empty' | 'loading' | 'unavailable' | 'ready'

export const estimateKey = (item: Item) =>
  JSON.stringify([item.qty, item.category, item.description, item.weightKg, item.lengthCm, item.widthCm, item.heightCm])

export type EstimateFailure = Extract<EstimateState, { status: 'unavailable' }>

const retryTime = (at: number) =>
  new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

/** A failed estimate as the error banner words it: f-edge's own error when it sent one, ours otherwise. */
export function estimateFailureText(failure: EstimateFailure): { headline: string; lines: string[] } {
  switch (failure.reason) {
    case 'rate_limited':
      return {
        headline:
          failure.retryAt === undefined
            ? t('estimate.rate-limited')
            : t('estimate.rate-limited-until', { time: retryTime(failure.retryAt) }),
        lines: [],
      }
    case 'access':
      return { headline: t('estimate.access'), lines: [] }
    case 'invalid_item':
      return { headline: t('estimate.invalid-item'), lines: [] }
    case 'temporary':
      return failure.backend
        ? formatBackendError(failure.backend, itemIssueLine)
        : { headline: t('estimate.temporary'), lines: [] }
    default:
      return failure.reason satisfies never
  }
}

/** The item a Save could not price: its row, or `null` for a new item, and why. */
export type Unpriced = { index: number | null; failure: EstimateFailure }

/** Every missing price in one list: rows priced from a draft, and the item the open editor could not save. */
export function estimateFailureList(
  rows: readonly Item[],
  estimates: Record<string, EstimateState>,
  unpriced?: Unpriced
) {
  const fromRows = rows.flatMap((row, index) => {
    const state = estimates[estimateKey(row)]
    // The editor's own failure stands for its row, so the row is not listed twice.
    return state?.status === 'unavailable' && unpriced?.index !== index
      ? [{ label: t('item.number', { number: index + 1 }), ...estimateFailureText(state) }]
      : []
  })
  if (!unpriced) return fromRows
  const label = unpriced.index === null ? t('item.new') : t('item.number', { number: unpriced.index + 1 })
  return [...fromRows, { label, ...estimateFailureText(unpriced.failure) }]
}
